import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useUiStore } from '@/store/ui';
import {
  chainGet,
  fmtDpb,
  fmtTime,
  shortAddr,
  type ChainStatus,
  type ChainBlock,
  type ChainAccount,
} from '@/chainApi';

/** 链浏览器：DP 链区块/交易/账户无缝集成（数据经服务端 /chain-api 代理）。 */
export function ChainExplorerPage(): JSX.Element {
  const [status, setStatus] = useState<ChainStatus | null>(null);
  const [blocks, setBlocks] = useState<ChainBlock[]>([]);
  const [accounts, setAccounts] = useState<ChainAccount[]>([]);
  const [mempool, setMempool] = useState<unknown[]>([]);
  const [detail, setDetail] = useState<{ index: number; raw: Record<string, unknown> } | null>(null);
  const [loading, setLoading] = useState(false);
  const [qAddr, setQAddr] = useState('');
  const [qResult, setQResult] = useState<Record<string, unknown> | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const [st, bl, ac, mp] = await Promise.all([
        chainGet<{ ok: boolean; data: ChainStatus }>('api/v1/status'),
        chainGet<{ blocks: ChainBlock[] }>('api/blocks?n=15'),
        chainGet<{ accounts: ChainAccount[] }>('api/accounts?n=20'),
        chainGet<{ mempool: unknown[] }>('api/mempool'),
      ]);
      setStatus(st.data);
      setBlocks(bl.blocks ?? []);
      setAccounts(ac.accounts ?? []);
      setMempool(mp.mempool ?? []);
    } catch (err) {
      useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '未知错误');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openBlock = async (index: number): Promise<void> => {
    try {
      const raw = await chainGet<Record<string, unknown>>('api/block/' + index);
      setDetail({ index, raw });
    } catch (err) {
      useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '未知错误');
    }
  };

  const queryAddr = async (): Promise<void> => {
    const a = qAddr.trim().toLowerCase();
    if (!/^0x[0-9a-f]{40}$/.test(a)) {
      useUiStore.getState().pushToast('error', '地址格式：0x + 40 位十六进制');
      return;
    }
    try {
      const raw = await chainGet<Record<string, unknown>>('api/account/' + a);
      setQResult(raw);
    } catch (err) {
      useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '未知错误');
    }
  };

  const txs = detail ? (Array.isArray(detail.raw.transactions) ? detail.raw.transactions : []) : [];

  return (
    <div className="grid gap-4">
      <div className="card p-5">
        <div className="flex items-center justify-between">
          <h1 className="text-base font-medium text-text">链浏览器 · DP Chain</h1>
          <div className="flex gap-2">
            <Badge tone={status?.producing ? 'ok' : 'warn'}>{status?.producing ? '出块中' : '暂停'}</Badge>
            <Button size="sm" variant="ghost" loading={loading} onClick={() => void load()}>
              刷新
            </Button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="当前高度" value={status ? String(status.height) : '—'} />
          <Stat label="出块间隔" value={status ? `${status.interval}s` : '—'} />
          <Stat label="1 分钟出块" value={status ? String(status.block_rate_1m) : '—'} />
          <Stat label="总供应" value={status ? fmtDpb(status.total_supply) : '—'} />
          <Stat label="已释放" value={status ? fmtDpb(status.released) : '—'} />
          <Stat label="内存池" value={status ? String(status.mempool_size) : '—'} />
        </div>
        {status?.pools ? (
          <div className="mt-3 flex flex-wrap gap-2 text-xs text-dim">
            <span>flow 池 {fmtDpb(status.pools.flow ?? 0)}</span>
            <span>· stable 池 {fmtDpb(status.pools.stable ?? 0)}</span>
            <span>· treasury {fmtDpb(status.pools.treasury ?? 0)}</span>
            <span>· 最新块 {fmtTime(status.latest_block_timestamp)}</span>
          </div>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-medium text-text">区块（最新在前）</h2>
          <div className="max-h-[420px] space-y-2 overflow-y-auto">
            {blocks.length === 0 ? <p className="py-2 text-xs text-dim">暂无区块</p> : null}
            {blocks.map((b) => (
              <button
                key={b.index}
                type="button"
                onClick={() => void openBlock(b.index)}
                className="w-full rounded-btn bg-panel-2 px-3 py-2 text-left text-xs hover:bg-line"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-accent">#{b.index}</span>
                  <span className="text-dim">{fmtTime(b.timestamp)}</span>
                  <span className="text-dim">{b.transactions.length} tx</span>
                </div>
                <div className="mt-1 truncate font-mono text-[11px] text-dim">
                  {shortAddr(b.hash)} · {shortAddr(b.proposer)}
                </div>
              </button>
            ))}
          </div>
          {mempool.length > 0 ? (
            <p className="mt-2 text-xs text-dim">内存池 {mempool.length} 笔待打包</p>
          ) : null}
        </div>

        <div className="card p-5">
          <h2 className="mb-3 text-sm font-medium text-text">
            {detail ? `区块 #${detail.index} 详情` : '点击左侧区块查看详情'}
          </h2>
          {detail ? (
            <div className="space-y-2 text-xs">
              <KV k="Hash" v={String(detail.raw.hash ?? '')} />
              <KV k="Prev" v={String(detail.raw.prev_hash ?? '')} />
              <KV k="Merkle" v={String(detail.raw.merkle_root ?? '')} />
              <KV k="Proposer" v={String(detail.raw.proposer ?? '')} />
              <KV k="时间" v={fmtTime(Number(detail.raw.timestamp ?? 0))} />
              <KV k="交易数" v={String(txs.length)} />
              {txs.length > 0 ? (
                <div>
                  <p className="mb-1 font-medium text-text">交易明细</p>
                  <div className="max-h-[220px] space-y-1 overflow-y-auto">
                    {txs.map((t, i) => (
                      <pre key={i} className="whitespace-pre-wrap rounded-btn bg-panel-2 p-2 font-mono text-[11px] text-dim">
                        {JSON.stringify(t, null, 1)}
                      </pre>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="py-2 text-xs text-dim">区块为空块（无交易）时交易数显示 0</p>
          )}
        </div>
      </div>

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">账户</h2>
        <div className="mb-3 flex gap-2">
          <Input
            value={qAddr}
            onChange={(e) => setQAddr(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void queryAddr();
            }}
            placeholder="0x… 查询任意地址"
            className="font-mono"
          />
          <Button size="sm" onClick={() => void queryAddr()}>
            查询
          </Button>
        </div>
        {qResult ? (
          <div className="mb-3 rounded-btn bg-panel-2 p-3 text-xs">
            <p className="font-medium text-text">{String(qResult.address ?? '')}</p>
            <p className="mt-1 text-dim">
              DPB 余额 <span className="text-ok">{fmtDpb(Number(qResult.balance ?? 0))}</span>
              {qResult.dpx != null ? (
                <span className="ml-3">
                  DPX <span className="text-ok">{fmtDpb(Number(qResult.dpx))}</span>
                </span>
              ) : null}
              {qResult.nonce != null ? <span className="ml-3 text-dim">nonce {String(qResult.nonce)}</span> : null}
            </p>
          </div>
        ) : null}
        <div className="max-h-[260px] space-y-1 overflow-y-auto">
          {accounts.map((a) => (
            <div key={a.address} className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-1.5 text-xs">
              <button type="button" className="font-mono text-dim hover:text-accent" onClick={() => { setQAddr(a.address); void queryAddr(); }}>
                {shortAddr(a.address)}
              </button>
              <span className="text-ok">{fmtDpb(Number(a.balance ?? 0))} DPB</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-btn bg-panel-2 px-3 py-2">
      <p className="text-[11px] text-dim">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-text">{value}</p>
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex items-start gap-2">
      <span className="w-16 shrink-0 text-dim">{k}</span>
      <span className="min-w-0 break-all font-mono text-[11px] text-text">{v || '—'}</span>
    </div>
  );
}

export default ChainExplorerPage;
